import { Injectable } from '@angular/core';
import { HttpHeaders, HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

import { MapService, MapServiceList, AnonMapService } from '../models/map-services.model';
import { CustomerList } from '../admin/customers/customer.model';
import { environment } from '../../../environments/environment';
import { AuthService } from '../auth.service';

@Injectable()
export class MapServicesService {
  constructor(private http: HttpClient, private authService: AuthService) {}

  /**
   * Returns headers with token
   * @returns {HttpHeaders}
   */
  getAuthHeaders(): HttpHeaders {
    return this.authService.getHeadersWithToken();
  }

  /**
   * Creates and saves a mapService
   * @param {Dataset} mapService - MapService to create
   * @returns {Observable<MapService>}
   */
  createMapService(mapService: MapService): Observable<MapService> {
    return this.http.put<MapService>(`${environment.apiBaseURL}/admin/mapservice`, mapService, {
      headers: this.getAuthHeaders(),
    });
  }

  /**
   * Get list of mapServices, filtered by optional search term
   * @param {page} Page number to return
   * @param {searchTerm} Filter list of mapservices on a search term
   * @returns {Observable<MapServiceList>}
   */
  getMapServices(page: number, searchTerm: string): Observable<MapServiceList> {
    let params = new HttpParams();
    params = params.append('page', page.toString());
    params = params.append('textSearch', searchTerm);
    return this.http.get<MapServiceList>(`${environment.apiBaseURL}/admin/mapservice/list`, {
      params: params,
      headers: this.getAuthHeaders(),
    });
  }

  /** Get list of mapServices, filtered by optional search term
   * @param {page} Page number to return
   * @param {searchTerm} Filter list of mapservices on a search term
   * @returns {Observable<MapServiceList>}
   */
  getAnonMapServicesList(page: number, searchTerm: string): Observable<MapServiceList> {
    let params = new HttpParams();
    params = params.append('page', page.toString());
    params = params.append('textSearch', searchTerm);
    return this.http.get<MapServiceList>(`${environment.apiBaseURL}/mapservice/list`, {
      params: params,
    });
  }

  /**
   * Gets mapService matching mapServiceId
   * @param {number} mapServiceId
   * @returns {Observable<MapServiceList>}
   */
  getMapService(mapServiceId: number): Observable<MapService> {
    return this.http.get<MapService>(`${environment.apiBaseURL}/admin/mapservice/${mapServiceId}`, {
      headers: this.getAuthHeaders(),
    });
  }

  /**
   * Get Image
   * @param url
   * @returns {Observable<Blob>}
   */
  getImage(url: string) {
    return this.http.get(url, {
      responseType: 'blob',
    });
  }

  /**
   * Gets service matching service slug (unique identifier)
   * @param {number} serviceSlug
   * @returns {Observable<MapService>}
   */
  getAnonMapServiceDetails(serviceSlug: string): Observable<AnonMapService> {
    return this.http.get<AnonMapService>(`${environment.apiBaseURL}/mapservice/${serviceSlug}`);
  }

  /**
   * Updates the supplied mapService
   * @param {number} mapServiceId
   * @param {MapService} mapService
   * @returns {Observable<MapService>}
   */
  updateMapService(mapservice: MapService): Observable<MapService> {
    return this.http.post<MapService>(
      `${environment.apiBaseURL}/admin/mapservice/${mapservice.mapServiceId}`,
      mapservice,
      { headers: this.getAuthHeaders() },
    );
  }

  /**
   * Delete a mapService
   * @param mapServiceId
   * @returns {Observable<Object>}
   */
  deleteMapService(mapServiceId: number) {
    return this.http.delete(`${environment.apiBaseURL}/admin/mapservice/${mapServiceId}`, {
      headers: this.getAuthHeaders(),
    });
  }

  /**
   * Associates a dataset with a mapService
   * @param mapServiceId
   * @param datasetId
   * @returns {Observable<MapService>}
   */
  associateDataset(mapServiceId: number, datasetId: number): Observable<MapService> {
    return this.http.post<MapService>(
      `${environment.apiBaseURL}/admin/mapservice/${mapServiceId}/associate-dataset/${datasetId}`,
      '',
      { headers: this.getAuthHeaders() },
    );
  }

  /**
   * Dissociates a dataset from a mapService
   * @param mapServiceId
   * @param datasetId
   * @returns {Observable<MapService>}
   */
  dissociateDataset(mapServiceId: number, datasetId: number): Observable<MapService> {
    return this.http.post<MapService>(
      `${environment.apiBaseURL}/admin/mapservice/${mapServiceId}/dissociate-dataset/${datasetId}`,
      '',
      { headers: this.getAuthHeaders() },
    );
  }

  /**
   * Get the customers associated with a map service
   */
  getAssociatedCustomers(mapServiceId: number): Observable<CustomerList> {
    return this.http.get<CustomerList>(
      `${environment.apiBaseURL}/admin/mapservice/${mapServiceId}/customers`,
      { headers: this.getAuthHeaders() },
    );
  }
}
